/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

/**
 * Tabbable Mode helpers
 */
import head from 'ramda/src/head';
import last from 'ramda/src/last';
import pipe from 'ramda/src/pipe';
import sort from 'ramda/src/sort';
// Query selector to return all tabbable elements
const tabbableQuery = [
  'a:not([name]):not([disabled])',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  "[tabIndex]:not([tabIndex='-1']"
].join(', ');

// uses tabbableQuery selector to determine if an element is tabbable
const isTabbableElement = (element: HTMLElement) => element.matches(tabbableQuery);

// All tabbable child elements in a node
const allTabbableElements = (el: HTMLElement) =>
  Array.from(el.querySelectorAll(tabbableQuery)).filter((element) => {
    // @ts-ignore
    // this is to ensure the input isn't hidden
    return element.offsetParent !== null && element.tabIndex !== -1;
  }) as HTMLElement[];

// sort compare function for tab indexes
const sortByTabIndex = <T extends HTMLElement>(a: T, b: T) => {
  const idxA = a.tabIndex ?? 0;
  const idxB = b.tabIndex ?? 0;
  return idxA - idxB;
};

// All tabbable child elements in a node,  or the container itself if there is none
const tabbablesOrContainer = (element: HTMLElement) => {
  const tabbableElements = allTabbableElements(element);
  return tabbableElements.length ? tabbableElements : [element];
};

// First tabbable child element in a node, or the container itself if there is none
const firstTabbableOrContainer = pipe(tabbablesOrContainer, sort(sortByTabIndex), head);

/**
 * Focus Helpers
 */

// preventScroll safe focus setter
const focusOn = (element: HTMLElement) => {
  element.focus({ preventScroll: true });
};

// focus on first tabbable in a container or container itself if there is none
const focusWithin = (element: HTMLElement) => {
  const firstTabbable = firstTabbableOrContainer(element);
  focusOn(firstTabbable as HTMLElement);
};

// focus on initial tabstop
const focusOnStart = pipe(tabbablesOrContainer, sort(sortByTabIndex), head, focusOn);

// focus on last tabstop
const focusOnEnd = pipe(tabbablesOrContainer, sort(sortByTabIndex), last, focusOn);

// get active element from document parent of the node
const getActiveElement = (node?: Node) => {
  const conditionalDocument = node?.ownerDocument ?? document;
  return conditionalDocument.activeElement as HTMLElement;
};

export {
  focusOn, // preventScroll safe focus setter
  focusWithin, // focus on first tabbable in a container or container itself if there is none
  focusOnEnd, // focus on first tabstop in a container
  focusOnStart, // focus on last tabstop in a container
  getActiveElement, // get active element from document parent of the node
  isTabbableElement // uses tabbableQuery selector to determine if an element is tabbable
};
